import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, GeoJSON, Marker, Popup, useMap, Rectangle } from 'react-leaflet';
import { FaLayerGroup } from 'react-icons/fa';
import L from 'leaflet';
import ReactDOM from 'react-dom/client';

// Import utility and data files
import '../utils/leafletIcons';
import { fieldBoundaries } from '../data/fieldBoundaries';
import { sensorData } from '../data/sensorData';
import { baseLayers, overlayLayers } from '../data/mapLayers';
import { sensorIcon, waterIcon, alertIcon } from '../utils/leafletIcons';
import { getCropTypeInfo } from '../utils/croplandCrosAPI';

// Import custom components
import SensorPopup from './SensorPopup';
import FieldPopup from './FieldPopup';
import MapLayerControl from './MapLayerControl';
import MapLegend from './MapLegend';

// Component to handle map layer changes
const MapLayerManager = ({ activeLayers, opacityValues }) => {
  const map = useMap();
  
  useEffect(() => {
    console.log('[MapLayerManager] Layer update started:', { activeLayers, opacityValues });
    const startTime = window.performance.now();
    
    // Track active layer instances for cleanup
    const activeInstances = new Set();
    
    // Handle layer updates
    Object.entries(activeLayers).forEach(([key, isActive]) => {
      if (isActive && overlayLayers[key]) {
        const layer = overlayLayers[key];
        if (layer.instance) {
          layer.instance.setOpacity(opacityValues[key] || 1);
          activeInstances.add(layer.instance);
        }
      }
    });
    
    return () => {
      const endTime = window.performance.now();
      console.log('[MapLayerManager] Layer update completed in:', endTime - startTime, 'ms');
      
      // Cleanup unused layer instances
      Object.values(overlayLayers).forEach(layer => {
        if (layer.instance && !activeInstances.has(layer.instance)) {
          map.removeLayer(layer.instance);
          layer.instance = null;
        }
      });
    };
  }, [activeLayers, opacityValues, map]);
  
  return null;
};

// Mock overlay components for visualization layers
const NDVILayer = ({ opacity }) => {
  // DMV area NDVI layer (simplified mock)
  const bounds = [
    [38.85, -77.2], // Southwest corner
    [39.0, -76.9]   // Northeast corner
  ];
  
  return (
    <Rectangle 
      bounds={bounds} 
      pathOptions={{ 
        color: 'transparent',
        fillColor: '#91cf60', 
        fillOpacity: opacity 
      }} 
    />
  );
};

const EVILayer = ({ opacity }) => {
  // DMV area EVI layer (simplified mock)
  const bounds = [
    [38.7, -77.3], // Southwest corner
    [38.9, -77.0]  // Northeast corner
  ];
  
  return (
    <Rectangle 
      bounds={bounds} 
      pathOptions={{ 
        color: 'transparent',
        fillColor: '#4575b4', 
        fillOpacity: opacity 
      }} 
    />
  );
};

const ThermalLayer = ({ opacity }) => {
  // DMV area Thermal layer (simplified mock)
  const bounds = [
    [38.8, -77.4], // Southwest corner
    [39.1, -77.0]  // Northeast corner
  ];
  
  return (
    <Rectangle 
      bounds={bounds} 
      pathOptions={{ 
        color: 'transparent',
        fillColor: '#d73027', 
        fillOpacity: opacity 
      }} 
    />
  );
};

const NDRELayer = ({ opacity }) => {
  const bounds = [
    [41.86, 12.51], // Southwest corner
    [41.89, 12.55]  // Northeast corner
  ];
  
  return (
    <Rectangle 
      bounds={bounds} 
      pathOptions={{ 
        color: 'transparent',
        fillColor: '#4d9221', 
        fillOpacity: opacity 
      }} 
    />
  );
};

// Add a fallback tile layer component
const FallbackTileLayer = () => {
  return (
    <TileLayer
      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      attribution="© OpenStreetMap contributors"
      maxZoom={19}
    />
  );
};

const MapView = ({ onToggleLayersPanel }) => {
  const [showLayerControl, setShowLayerControl] = useState(false);
  const [activeLayers, setActiveLayers] = useState(() => ({
    basemap: 'satellite',
    sensors: true,
    fields: true,
    ndvi: false,
    evi: false,
    thermal: false,
    ndre: false
  }));
  
  useEffect(() => {
    console.log('[MapView] Component mounted');
    const startTime = window.performance.now();
    
    // Create performance observer for long tasks
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry) => {
        if (entry.duration > 50) { // Log tasks taking more than 50ms
          console.warn('[Performance] Long task detected:', {
            name: entry.name,
            duration: Math.round(entry.duration) + 'ms',
            startTime: Math.round(entry.startTime) + 'ms'
          });
        }
      });
    });
    
    // Observe long tasks and resource timing
    observer.observe({ entryTypes: ['longtask', 'resource'] });
    
    return () => {
      const endTime = window.performance.now();
      console.log('[MapView] Component unmounted after:', endTime - startTime, 'ms');
      observer.disconnect();
    };
  }, []);

  const [opacityValues, setOpacityValues] = useState(() => ({
    ndvi: 0.7,
    evi: 0.7,
    thermal: 0.7,
    ndre: 0.7
  }));
  
  const [activeAnalysisLayer, setActiveAnalysisLayer] = useState(null);
  
  const handleLayerChange = React.useCallback((newActiveLayers, newOpacityValues) => {
    console.log('[MapView] Layer change triggered:', { newActiveLayers, newOpacityValues });
    const startTime = window.performance.now();
    
    // Batch state updates
    ReactDOM.unstable_batchedUpdates(() => {
      setActiveLayers(newActiveLayers);
      setOpacityValues(newOpacityValues);
      
      // Set the active analysis layer for the legend
      const analysisLayers = ['ndvi', 'evi', 'thermal', 'ndre'];
      const activeAnalysis = analysisLayers.find(layer => newActiveLayers[layer]);
      setActiveAnalysisLayer(activeAnalysis || (newActiveLayers.fields ? 'crops' : null));
    });
    
    const endTime = window.performance.now();
    console.log('[MapView] Layer change completed in:', endTime - startTime, 'ms');
  }, []);
  
  // Function to style GeoJSON features
  const fieldStyle = React.useCallback((feature) => {
    // Get crop color from the utility if available
    const cropType = feature.properties.crop;
    const cropInfo = cropType ? getCropTypeInfo(cropType) : null;
    const cropColor = cropInfo ? cropInfo.color : '#4caf50'; // Default to green if no match

    return {
      color: feature.properties.status === 'good' ? cropColor :
             feature.properties.status === 'warning' ? '#ffc107' : '#f44336',
      weight: 2,
      opacity: 0.8,
      fillOpacity: 0.2,
      fillColor: cropColor
    };
  }, []);

  // Function to get the appropriate icon for a sensor
  const getSensorIcon = React.useCallback((sensor) => {
    if (sensor.alert) {
      return alertIcon;
    }
    if (sensor.type === 'irrigation') {
      return waterIcon;
    }
    return sensorIcon;
  }, []);
  
  const [mapError, setMapError] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);

  // Memoize the tile layer to prevent unnecessary re-renders
  const BaseTileLayer = React.useMemo(() => {
    const baseLayerUrl = baseLayers[activeLayers.basemap]?.url || baseLayers.osm.url;
    const baseLayerAttribution = baseLayers[activeLayers.basemap]?.attribution || baseLayers.osm.attribution;
    const baseLayerSubdomains = baseLayers[activeLayers.basemap]?.subdomains || [];

    return (
      <TileLayer
        url={baseLayerUrl}
        attribution={baseLayerAttribution}
        subdomains={baseLayerSubdomains}
        maxZoom={20}
        eventHandlers={{
          loading: () => {
            console.log('[TileLayer] Tiles loading started');
            window.performance.mark('tilesLoadStart');
            setMapLoaded(false);
          },
          load: () => {
            console.log('[TileLayer] Tiles loaded successfully');
            window.performance.mark('tilesLoadEnd');
            window.performance.measure('Tile Load Time', 'tilesLoadStart', 'tilesLoadEnd');
            setMapLoaded(true);
            setMapError(false);
          },
          error: (error) => {
            console.error('[TileLayer] Error loading tiles:', error);
            setMapError(true);
          }
        }}
      />
    );
  }, [activeLayers.basemap]);
  
  useEffect(() => {
    // Memory usage monitoring
    const memoryInterval = setInterval(() => {
      if (window.performance && window.performance.memory) {
        const { usedJSHeapSize, totalJSHeapSize } = window.performance.memory;
        console.log('[Memory Usage]', {
          used: Math.round(usedJSHeapSize / 1024 / 1024) + 'MB',
          total: Math.round(totalJSHeapSize / 1024 / 1024) + 'MB',
          percentage: Math.round((usedJSHeapSize / totalJSHeapSize) * 100) + '%'
        });
      }
    }, 5000);

    return () => clearInterval(memoryInterval);
  }, []);

  // Function to create popup content
  const createPopupContent = React.useCallback((feature) => {
    const container = L.DomUtil.create('div');
    const root = document.createElement('div');
    container.appendChild(root);
    
    const reactRoot = ReactDOM.createRoot(root);
    reactRoot.render(<FieldPopup feature={feature} />);
    
    return container;
  }, []);

  // Memoize GeoJSON to prevent unnecessary re-renders
  const FieldBoundariesLayer = React.useMemo(() => (
    activeLayers.fields && (
      <GeoJSON 
        data={fieldBoundaries} 
        style={fieldStyle}
        onEachFeature={(feature, layer) => {
          layer.bindPopup(() => createPopupContent(feature));
        }}
      />
    )
  ), [activeLayers.fields, fieldStyle, createPopupContent]);

  // Memoize sensors layer to prevent unnecessary re-renders
  const SensorsLayer = React.useMemo(() => (
    activeLayers.sensors && sensorData.map(sensor => (
      <Marker
        key={sensor.id}
        position={sensor.location}
        icon={getSensorIcon(sensor)}
      >
        <Popup>
          <SensorPopup sensor={sensor} />
        </Popup>
      </Marker>
    ))
  ), [activeLayers.sensors, getSensorIcon]);

  return (
    <div className="map-container">
      <MapContainer 
        center={[38.9072, -77.0369]} // Center on Washington DC
        zoom={10} // Zoom out to show more of the DMV area
        style={{ height: '100%', width: '100%' }}
        zoomControl={false}
        whenCreated={(map) => {
          console.log('[MapContainer] Map instance created');
          // Store map instance for potential debugging
          window.leafletMap = map;
        }}
      >
        {/* Base Tile Layer with fallback */}
        {mapError ? <FallbackTileLayer /> : BaseTileLayer}
        
        {/* Analysis Layers */}
        {activeLayers.ndvi && <NDVILayer opacity={opacityValues.ndvi} />}
        {activeLayers.evi && <EVILayer opacity={opacityValues.evi} />}
        {activeLayers.thermal && <ThermalLayer opacity={opacityValues.thermal} />}
        {activeLayers.ndre && <NDRELayer opacity={opacityValues.ndre} />}
        
        {/* Field Boundaries */}
        {FieldBoundariesLayer}
        
        {/* Sensors */}
        {SensorsLayer}
        
        {/* Layer Manager Component */}
        <MapLayerManager 
          activeLayers={activeLayers} 
          opacityValues={opacityValues} 
        />
      </MapContainer>
      
      {/* Loading indicator */}
      {!mapLoaded && (
        <div 
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'rgba(0,0,0,0.7)',
            color: 'white',
            padding: '10px 20px',
            borderRadius: '4px',
            zIndex: 1000
          }}
        >
          Loading map...
        </div>
      )}
      
      {/* Map Controls */}
      <div className="map-controls">
        <button 
          className="map-control-button" 
          onClick={() => setShowLayerControl(!showLayerControl)}
        >
          <FaLayerGroup />
        </button>
      </div>
      
      {/* Layer Control Panel */}
      {showLayerControl && (
        <MapLayerControl 
          layers={baseLayers} 
          onLayerChange={handleLayerChange} 
          onClose={() => setShowLayerControl(false)} 
        />
      )}
      
      {/* Map Legend */}
      <MapLegend activeLayer={activeAnalysisLayer || (activeLayers.fields ? 'crops' : null)} />
      
      {/* Sentinel hub attribution */}
      <div 
        style={{
          position: 'absolute',
          bottom: '8px',
          right: '8px',
          fontSize: '10px',
          color: 'white',
          backgroundColor: 'rgba(0,0,0,0.5)',
          padding: '2px 6px',
          borderRadius: '4px',
          zIndex: 1000
        }}
      >
        Sentinel Hub: 2023-11-01 02:21:17
      </div>
    </div>
  );
};

export default MapView; 